import {
  TechnologyDistributionAsset,
  TechnologyDistributionHosting,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
  TECHNOLOGY_INFRASTRUCTURE_KIND_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

export type DistributionFilters = {
  productId: string;
  environmentKind: string;
  assetId: string;
  search: string;
};

export type VisibleAsset = {
  asset: TechnologyDistributionAsset;
  hostings: TechnologyDistributionHosting[];
};

export type ApplicationPlacement = {
  asset: TechnologyDistributionAsset;
  hosting: TechnologyDistributionHosting;
};

export type DistributionApplicationGroup = {
  applicationId: string;
  applicationName: string;
  products: TechnologyDistributionHosting['products'];
  placements: ApplicationPlacement[];
};

export type UtilizationBand = 'unknown' | 'low' | 'moderate' | 'high' | 'critical';

const quantityFormat = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
const percentFormat = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

const BAND_SEVERITY: Record<UtilizationBand, number> = {
  unknown: -1,
  low: 0,
  moderate: 1,
  high: 2,
  critical: 3,
};

function fold(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function formatCpu(cores: number | null | undefined): string {
  if (cores == null) return 'Não informado';
  const formatted = quantityFormat.format(cores);
  return cores === 1 ? `${formatted} vCPU` : `${formatted} vCPUs`;
}

export function formatGb(value: number | null | undefined): string {
  if (value == null) return 'Não informado';
  return `${quantityFormat.format(value)} GB`;
}

export function utilizationBand(percent: number | null | undefined): UtilizationBand {
  if (percent == null || Number.isNaN(percent)) return 'unknown';
  if (percent >= 90) return 'critical';
  if (percent >= 70) return 'high';
  if (percent >= 40) return 'moderate';
  return 'low';
}

export function utilizationLabel(percent: number | null | undefined): string {
  if (percent == null || Number.isNaN(percent)) return 'Não monitorado';
  return `${percentFormat.format(percent)}%`;
}

export function worstUtilization(asset: TechnologyDistributionAsset): UtilizationBand {
  const bands = [
    utilizationBand(asset.cpuUtilizationPercent),
    utilizationBand(asset.memoryUtilizationPercent),
    utilizationBand(asset.diskUtilizationPercent),
  ];
  return bands.reduce((worst, band) =>
    BAND_SEVERITY[band] > BAND_SEVERITY[worst] ? band : worst,
  );
}

export function bandLabel(band: UtilizationBand): string {
  switch (band) {
    case 'low':
      return 'Baixa utilização';
    case 'moderate':
      return 'Uso moderado';
    case 'high':
      return 'Alta utilização';
    case 'critical':
      return 'Crítico';
    default:
      return 'Não monitorado';
  }
}

export function isUnmonitored(asset: TechnologyDistributionAsset): boolean {
  return worstUtilization(asset) === 'unknown';
}

export function environmentLabel(kind: string): string {
  return technologyOptionLabel(TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS, kind);
}

export function infrastructureKindLabel(kind: string): string {
  return technologyOptionLabel(TECHNOLOGY_INFRASTRUCTURE_KIND_OPTIONS, kind);
}

export function singleEnvironmentKind(
  hostings: readonly { environmentKind: string }[],
): string | null {
  if (hostings.length === 0) return null;
  const first = hostings[0].environmentKind;
  for (let index = 1; index < hostings.length; index += 1) {
    if (hostings[index].environmentKind !== first) return null;
  }
  return first;
}

export function environmentSummary(
  hostings: readonly { environmentKind: string }[],
): string | null {
  if (hostings.length === 0) return null;
  const kind = singleEnvironmentKind(hostings);
  return kind ? environmentLabel(kind) : 'Vários ambientes';
}

export function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function paletteIndex(id: string, size: number): number {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash + id.charCodeAt(index) * (index + 1)) % 997;
  }
  return size === 0 ? 0 : hash % size;
}

export function chipSubtitle(
  hosting: TechnologyDistributionHosting,
  siblings: readonly TechnologyDistributionHosting[],
): string {
  const label = environmentLabel(hosting.environmentKind);
  const duplicates = siblings.filter(
    (item) =>
      item.applicationId === hosting.applicationId
      && item.environmentKind === hosting.environmentKind,
  ).length;
  if (duplicates > 1 && hosting.address) return `${label} · ${hosting.address}`;
  return label;
}

function hostingInScope(
  hosting: TechnologyDistributionHosting,
  filters: DistributionFilters,
): boolean {
  if (filters.environmentKind && hosting.environmentKind !== filters.environmentKind) return false;
  if (filters.productId && !hosting.products.some((product) => product.id === filters.productId)) {
    return false;
  }
  return true;
}

function assetMatchesTerm(asset: TechnologyDistributionAsset, term: string): boolean {
  return [
    asset.name,
    asset.vendor,
    asset.ipAddress,
    asset.operatingSystem,
    asset.region,
    infrastructureKindLabel(asset.kind),
  ].some((value) => fold(value).includes(term));
}

function hostingMatchesTerm(hosting: TechnologyDistributionHosting, term: string): boolean {
  return (
    fold(hosting.applicationName).includes(term)
    || fold(hosting.address).includes(term)
    || fold(environmentLabel(hosting.environmentKind)).includes(term)
    || hosting.products.some((product) => fold(product.name).includes(term))
  );
}

export function filterDistribution(
  assets: readonly TechnologyDistributionAsset[],
  filters: DistributionFilters,
): VisibleAsset[] {
  const term = fold(filters.search.trim());
  return assets
    .filter((asset) => !filters.assetId || asset.id === filters.assetId)
    .map((asset) => {
      const scoped = asset.hostings.filter((hosting) => hostingInScope(hosting, filters));
      const identityMatch = term.length > 0 && assetMatchesTerm(asset, term);
      const hostings =
        term.length === 0 || identityMatch
          ? scoped
          : scoped.filter((hosting) => hostingMatchesTerm(hosting, term));
      return { asset, hostings, identityMatch };
    })
    .filter((item) => {
      if (item.hostings.length > 0) return true;
      if (filters.environmentKind || filters.productId) return false;
      if (term.length > 0) return item.identityMatch;
      return true;
    })
    .map(({ asset, hostings }) => ({ asset, hostings }));
}

export function groupByApplication(items: readonly VisibleAsset[]): DistributionApplicationGroup[] {
  const groups = new Map<string, DistributionApplicationGroup>();
  for (const item of items) {
    for (const hosting of item.hostings) {
      const current = groups.get(hosting.applicationId);
      if (!current) {
        groups.set(hosting.applicationId, {
          applicationId: hosting.applicationId,
          applicationName: hosting.applicationName,
          products: hosting.products,
          placements: [{ asset: item.asset, hosting }],
        });
        continue;
      }
      current.placements.push({ asset: item.asset, hosting });
    }
  }

  const result: DistributionApplicationGroup[] = [];
  groups.forEach((group) => {
    result.push(group);
  });
  return result.sort((left, right) =>
    left.applicationName.localeCompare(right.applicationName, 'pt-BR'),
  );
}

export function countApplications(items: readonly VisibleAsset[]): number {
  const ids = new Set<string>();
  for (const item of items) {
    for (const hosting of item.hostings) ids.add(hosting.applicationId);
  }
  return ids.size;
}

export function countUnmonitored(items: readonly VisibleAsset[]): number {
  return items.filter((item) => isUnmonitored(item.asset)).length;
}

export function productNames(products: readonly { name: string }[]): string {
  return products.map((product) => product.name).join(', ');
}
